// MF-67 — Journal Categories list (Maestro-managed taxonomy). Standard row actions (preview / edit /
// delete-with-confirmation), server-ordered (order, name).
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { configPath } from '../paths'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { Button, Typography } from '@mui/material'
import RowActions from '../components/RowActions'
import ConfirmDelete from '../components/ConfirmDelete'
import PreviewDialog, { PreviewSection } from '../components/PreviewDialog'
import ListSearch from '../components/ListSearch'
import ListTable, { Stacked } from '../components/ListTable'
import VisibilityMatrix, { audiencesLabel } from '../components/VisibilityMatrix'
import { TypeBadge } from '../components/ColorBadge'
import { loadCategories } from '../../../../store/actions/journalCategoriesActions'
import { deleteCategory } from '../../../../services/journal/categories.service'

const CategoriesList = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { list, loading, totalRecords } = useSelector((state) => state.journalCategories.categories)
  const [toDelete, setToDelete] = useState(null)
  const [preview, setPreview] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [search, setSearch] = useState('')

  const reload = () =>
    dispatch(loadCategories({ page: page + 1, limit: rowsPerPage, ...(search ? { search } : {}) }))

  useEffect(() => {
    dispatch(loadCategories({ page: page + 1, limit: rowsPerPage, ...(search ? { search } : {}) }))
  }, [dispatch, page, rowsPerPage, search])

  const onConfirmDelete = async () => {
    setDeleting(true)
    try {
      await deleteCategory(toDelete._id)
      setToDelete(null)
      reload()
    } finally {
      setDeleting(false)
    }
  }

  const columns = [
    {
      key: 'name',
      label: 'Category',
      render: (r) => <Stacked primary={r.name} secondary={r.description} />
    },
    {
      key: 'type',
      label: 'Type',
      width: 160,
      render: (r) => <TypeBadge code={r.type || 'neutral'} />
    },
    {
      key: 'visibility',
      label: 'Visible to',
      width: 260,
      render: (r) => (
        <Stacked
          primary={`Graphs: ${audiencesLabel(r.visibility?.graph)}`}
          secondary={`Summary: ${audiencesLabel(r.visibility?.summary)}`}
        />
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 196,
      align: 'right',
      render: (r) => (
        <RowActions
          onPreview={() => setPreview(r)}
          onEdit={() => navigate(configPath('categories', `edit/${r._id}`))}
          onDelete={() => setToDelete(r)}
        />
      )
    }
  ]

  return (
    <>
      <div style={{ padding: '8px 8px 24px' }}>
        <ListSearch
          value={search}
          placeholder='Search categories…'
          onChange={(v) => {
            setSearch(v)
            setPage(0)
          }}
        >
          {!loading ? (
            <Typography style={{ fontSize: 13, color: 'rgb(100, 116, 139)' }}>
              {totalRecords} {totalRecords === 1 ? 'category' : 'categories'}
            </Typography>
          ) : null}
          <Button
            variant='contained'
            onClick={() => navigate(configPath('categories', 'add'))}
            startIcon={<FontAwesomeIcon icon={['fas', 'plus']} style={{ fontSize: 11 }} />}
          >
            New category
          </Button>
        </ListSearch>
        <ListTable
          columns={columns}
          rows={list}
          loading={loading}
          emptyText={search ? 'No categories match your search' : 'No categories yet'}
          pagination={{
            count: totalRecords,
            page,
            rowsPerPage,
            onPageChange: setPage,
            onRowsPerPageChange: (n) => {
              setRowsPerPage(n)
              setPage(0)
            }
          }}
        />
      </div>

      <ConfirmDelete
        open={Boolean(toDelete)}
        entity='category'
        name={toDelete?.name}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <PreviewDialog
        open={Boolean(preview)}
        title={preview?.name || 'Category'}
        onClose={() => setPreview(null)}
        header={
          preview ? (
            <>
              <TypeBadge code={preview.type || 'neutral'} size='md' />
            </>
          ) : null
        }
        rows={[{ label: 'Description', value: preview?.description, full: true }]}
      >
        {preview ? (
          <PreviewSection title='Who can see it'>
            <VisibilityMatrix value={preview.visibility} readOnly />
          </PreviewSection>
        ) : null}
      </PreviewDialog>
    </>
  )
}

export default CategoriesList
